// src/utils/policyHealth.js
//
// Source of truth for advisory messages on a policy. Drives the MSG light
// on the policy gauge.
//
// Messages stack: every rule is evaluated independently and pushes its own
// entry into messages[]. The overall `level` is the highest severity in
// the stack (red > yellow > green). Empty stack ⇒ green / NOMINAL.

import { parseLocalDate } from './parseLocalDate';

const DAY_MS = 86_400_000;
const RANK = { green: 0, yellow: 1, red: 2 };

export function getPolicyHealth(policy, opts = {}) {
  const messages = [];
  if (!policy) return { level: 'green', messages };

  const status = (policy.Status || '').toLowerCase();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp = parseLocalDate(policy.ExpirationDate);
  const daysToExp = exp ? Math.ceil((exp - today) / DAY_MS) : null;

  // ---- RED rules ----
  if ((status === 'active' || status === 'bound')
      && daysToExp !== null && daysToExp < 0) {
    messages.push({
      level: 'red',
      reason: `Policy term ended ${Math.abs(daysToExp)} days ago — status not updated`,
    });
  }
  if (status === 'active' && !policy.PolicyNumber) {
    messages.push({
      level: 'red',
      reason: 'Active policy missing policy number',
    });
  }

  // ---- YELLOW rules ----
  if (status === 'bound') {
    messages.push({
      level: 'yellow',
      reason: 'Bound — awaiting carrier-issued policy document',
    });
  }
  // Renewal-window rule excludes already-expired (handled by red rule above)
  // to avoid a duplicate yellow message for the same condition.
  if (status === 'active'
      && daysToExp !== null && daysToExp >= 0 && daysToExp <= 30) {
    messages.push({
      level: 'yellow',
      reason: `Renewal window — expires in ${daysToExp} days`,
    });
  }
  // Vault-PDF rule — inert until caller supplies vaultIndexReady + lookup fn.
  if (status === 'active'
      && opts.vaultIndexReady
      && typeof opts.policyHasVaultPdf === 'function'
      && !opts.policyHasVaultPdf(policy)) {
    messages.push({
      level: 'yellow',
      reason: 'Policy issued but documentation missing from vault',
    });
  }

  let level = 'green';
  for (const m of messages) {
    if (RANK[m.level] > RANK[level]) level = m.level;
  }
  return { level, messages };
}

// Worst level across a set of policies. Not consumed by the dashboard today
// (client card unchanged) — exported for future client-rollup use.
export function worstHealth(policies, opts) {
  let level = 'green';
  for (const p of (policies || [])) {
    const h = getPolicyHealth(p, opts);
    if (RANK[h.level] > RANK[level]) level = h.level;
  }
  return level;
}
